import * as React from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { Profile, ProfilePositionType } from '../types';
import { ProfilesService } from '../services/profiles-service';
import { ProfileHistory } from '../components/ProfileHistory';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';
import { 
  Calendar, 
  Download, 
  Edit, 
  Eye, 
  Info, 
  Trash2, 
  UserCircle
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const positionTypeLabels: Record<ProfilePositionType, string> = {
  executive_team: 'Executive Team',
  board_member: 'Board Member',
};

export function ProfileDetailsPage() {
  const { id } = useParams({ from: '/_authenticated/profiles/$id' });
  const [profile, setProfile] = React.useState<Profile | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        const profileData = await ProfilesService.getProfileById(id);
        setProfile(profileData);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setProfile(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <Alert variant="destructive">
        <Info className="h-4 w-4" />
        <AlertTitle>Profile Not Found</AlertTitle>
        <AlertDescription>
          The profile with ID {id} could not be found. It may have been deleted or you may not have permission to view it.
        </AlertDescription>
      </Alert>
    );
  }

  const createdDate = new Date(profile.created_at * 1000);
  const updatedDate = new Date(profile.updated_at * 1000);
  
  // Generate initials for avatar fallback
  const nameParts = profile.profile_full_name.split(' ');
  const initials = nameParts.length > 1 
    ? `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`
    : nameParts[0].substring(0, 2).toUpperCase();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <PageHeader
          title={profile.profile_full_name}
          description={profile.profile_title}
        />
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
          <Button variant="outline" size="sm">
            <Eye className="mr-2 h-4 w-4" />
            Preview
          </Button>
          <Button asChild size="sm">
            <Link to="/profiles/edit/$id" params={{ id: profile.id }}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Link>
          </Button>
          <Button 
            variant="destructive" 
            size="sm"
            onClick={async () => {
              if (window.confirm(`Are you sure you want to delete ${profile.profile_full_name}?`)) {
                try {
                  await ProfilesService.deleteProfile(id);
                  // Use the browser's built-in alert for simplicity
                  alert('Profile deleted successfully');
                  // Navigate back to profiles list
                  window.location.href = '/profiles';
                } catch (error) {
                  console.error('Error deleting profile:', error);
                  alert('Failed to delete profile');
                }
              }
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Badge 
          variant="outline" 
          className={
            profile.profile_position_type === 'executive_team'
              ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
              : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300'
          }
        >
          {positionTypeLabels[profile.profile_position_type]}
        </Badge>
        <Badge 
          variant="outline" 
          className={
            profile.profile_show_active
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
          }
        >
          {profile.profile_show_active ? 'Active' : 'Inactive'}
        </Badge>
        <Badge variant="outline">
          <Calendar className="mr-1 h-3 w-3" />
          Order: {profile.profile_order_weight}
        </Badge>
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-4 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Profile Photo</CardTitle>
                </CardHeader>
                <CardContent className="flex justify-center">
                  <Avatar className="h-40 w-40">
                    <AvatarImage src={profile.profile_photo} alt={profile.profile_full_name} />
                    <AvatarFallback className="text-4xl">{initials}</AvatarFallback>
                  </Avatar>
                </CardContent>
              </Card>
            </div>
            
            <div className="md:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Profile Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Full Name</span>
                    <span className="text-sm text-muted-foreground">{profile.profile_full_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Title</span>
                    <span className="text-sm text-muted-foreground">{profile.profile_title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Position Type</span>
                    <span className="text-sm text-muted-foreground">{positionTypeLabels[profile.profile_position_type]}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Display Order</span>
                    <span className="text-sm text-muted-foreground">{profile.profile_order_weight}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Status</span>
                    <span className="text-sm text-muted-foreground">{profile.profile_show_active ? 'Active' : 'Inactive'}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {profile.profile_summary && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{profile.profile_summary}</p>
              </CardContent>
            </Card>
          )}

          {profile.profile_bio && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Biography</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{profile.profile_bio}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="history" className="space-y-4 pt-4">
          {/* Basic profile timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Timeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="mr-4 mt-0.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary">
                      <UserCircle className="h-4 w-4 text-primary-foreground" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Profile Created</p>
                    <p className="text-xs text-muted-foreground">
                      {format(createdDate, 'PPpp')}
                    </p>
                  </div>
                </div>

                <Separator />

                <div className="flex items-start">
                  <div className="mr-4 mt-0.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                      <Edit className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Last Updated</p>
                    <p className="text-xs text-muted-foreground">
                      {format(updatedDate, 'PPpp')}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Detailed profile history */}
          <ProfileHistory profileId={id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
